import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Loader, Textarea, NumberInput, Slider, Text, Group, Stack, Tabs, Collapse } from '@mantine/core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faTimes, 
  faUpload, 
  faMicrophone, 
  faMusic, 
  faWandMagicSparkles, 
  faPlay, 
  faPause,
  faVolumeHigh,
  faDownload,
  faRefresh,
  faCheck,
  faChevronDown,
  faChevronUp,
  faLightbulb,
  faCopy,
  faFolderOpen,
  faEdit,
  faTrash
} from '@fortawesome/free-solid-svg-icons';
import { getApiUrl } from '../../config/api';
import { useNotification } from '../../context/NotificationContext';
import { projectService, PromptProject } from '../../services/projectService';
import { PromptBrowser } from '../shared';
import { useAuth } from '../../context/Auth/AuthContext';
import { getProxiedAudioUrl } from '../../utils/audioProxyUtil';
import { handleAudioDownload } from '../../utils/downloadUtil';
import styles from './AudioModal.module.css';
import CustomSlider from './CustomSlider';
import { downloadAudio } from '../../services/autoDownloadService';

interface AudioModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAudioGenerated: (audioUrl: string, projectId: string, type: 'melody') => void;
  type: 'upload' | 'melody' | 'prompts';
  currentProjectId?: string; // Renamed to avoid conflict with internal projectId state
  hideAllPromptsTab?: boolean; // New prop to hide "All Prompts" tab
}

interface GenerateMelodyRequest {
  prompt: string;
  duration?: number;
  temperature?: number;
  guidance_scale?: number;
  seed?: number;
  projectId?: string;
}

interface GenerateMelodyResponse {
  success: boolean;
  message: string;
  data: {
    projectId: string;
    prompt: string;
    melodies: Array<{
      index: number;
      seed: number;
      s3Url: string;
      duration: number;
      userLibraryS3Url?: string; // Phase 3: Optional field for user library URL
    }>;
    count: number;
  };
}

// Custom Select Component
interface CustomSelectProps {
  data: Array<{ value: string; label: string; description?: string }>;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
}

const CustomSelect: React.FC<CustomSelectProps> = ({ data, value, onChange, placeholder }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const selectRef = useRef<HTMLDivElement>(null);
  
  const selectedItem = data.find(item => item.value === value);
  const filteredData = data.filter(item => 
    item.label.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (item.description && item.description.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const handleSelect = (selectedValue: string) => {
    onChange(selectedValue);
    setIsOpen(false);
    setSearchTerm('');
  };

  // Close dropdown when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (selectRef.current && !selectRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  return (
    <div className={styles.customSelect} ref={selectRef}>
      <div 
        className={styles.selectTrigger} 
        onClick={() => setIsOpen(!isOpen)}
      >
        <span className={styles.selectValue}>
          {selectedItem ? selectedItem.label : placeholder || 'Select an option'}
        </span>
        <FontAwesomeIcon 
          icon={faChevronDown} 
          className={`${styles.selectArrow} ${isOpen ? styles.selectArrowOpen : ''}`}
        />
      </div>
      
      {isOpen && (
        <div className={styles.selectDropdown}>
          {data.length > 5 && (
            <input
              type="text"
              placeholder="Search..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className={styles.selectSearch}
              onClick={(e) => e.stopPropagation()}
            />
          )}
          <div className={styles.selectOptions}>
            {filteredData.map((item) => (
              <div
                key={item.value}
                className={`${styles.selectOption} ${item.value === value ? styles.selectOptionSelected : ''}`}
                onClick={() => handleSelect(item.value)}
              >
                <div className={styles.selectOptionLabel}>{item.label}</div>
                {item.description && (
                  <div className={styles.selectOptionDescription}>{item.description}</div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const AudioModal: React.FC<AudioModalProps> = ({ 
  isOpen, 
  onClose, 
  onAudioGenerated, 
  type,
  currentProjectId,
  hideAllPromptsTab = false // Default to false to maintain existing behavior
}) => {
  const { t } = useTranslation('audioModal');
  
  const [activeStep, setActiveStep] = useState<'generation' | 'result'>('generation');
  const [activeTab, setActiveTab] = useState<'melody' | 'library' | 'prompts'>(
    type === 'melody' ? 'melody' : 
    type === 'prompts' ? 'prompts' : 'melody'
  );
  const [isGenerating, setIsGenerating] = useState(false);
  
  // Melody form state
  const [melodyPrompt, setMelodyPrompt] = useState('');
  const [melodyDuration, setMelodyDuration] = useState(10);
  const [melodyTemperature, setMelodyTemperature] = useState(1.0);
  const [guidanceScale, setGuidanceScale] = useState(3.0);
  const [melodySeed, setMelodySeed] = useState<number | undefined>(undefined);
  
  // File upload state
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  // Result state
  const [generatedMelodies, setGeneratedMelodies] = useState<Array<{
    index: number;
    s3Url: string;
    duration: number;
    seed: number;
    userLibraryS3Url?: string;
  }>>([]);
  const [selectedMelodyIndex, setSelectedMelodyIndex] = useState<number>(0);
  const [playingTrackId, setPlayingTrackId] = useState<string | null>(null);
  const [currentAudio, setCurrentAudio] = useState<HTMLAudioElement | null>(null);
  const [projectId, setProjectId] = useState<string>('');
  
  // Accordion state
  const [isAdvancedOpen, setIsAdvancedOpen] = useState(false);
  
  const { showNotification } = useNotification();
  const { token } = useAuth();

  // Update active tab when modal opens with different type
  React.useEffect(() => {
    if (isOpen) {
      setActiveTab(type === 'melody' ? 'melody' : 
                  type === 'prompts' ? 'prompts' : 'melody');
      setActiveStep('generation');
    }
  }, [isOpen, type]);

  // Reset form when modal closes
  const handleClose = () => {
    if (currentAudio) {
      currentAudio.pause();
      setCurrentAudio(null);
    }
    setPlayingTrackId(null);
    setActiveStep('generation');
    setMelodyPrompt('');
    setGeneratedMelodies([]);
    setUploadedFile(null);
    onClose();
  };

  // Handle using prompt as melody description
  const handleUsePrompt = (prompt: string, project: PromptProject) => {
    setMelodyPrompt(prompt);
    setActiveTab('melody');
    // showNotification({
    //   title: 'Prompt Applied',
    //   message: 'Prompt has been applied to melody generation',
    //   type: 'success'
    // });
  };

  // Handle selecting soundtrack from library
  const handleSoundtrackSelect = (soundtrack: any) => {
    // Use the soundtrack directly in the current project
    onAudioGenerated(soundtrack.s3Url, currentProjectId || 'new', 'melody');
    // showNotification({
    //   title: '🎵 Soundtrack Added',
    //   message: `"${soundtrack.prompt}" has been added to your project`,
    //   type: 'success'
    // });
  };

  // Generate melody
  const generateMelody = async (): Promise<GenerateMelodyResponse> => {
    const request: GenerateMelodyRequest = {
      prompt: melodyPrompt,
      duration: melodyDuration,
      temperature: melodyTemperature,
      guidance_scale: guidanceScale,
      seed: melodySeed,
      projectId: currentProjectId, // Pass current project ID to prevent creating new project
    };

    const response = await fetch(getApiUrl('/music-generation/melody'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token && { 'Authorization': `Bearer ${token}` }),
      },
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
  };

  // Handle generation
  const handleGenerate = async () => {
    if (activeTab === 'melody' && !melodyPrompt.trim()) {
      showNotification({
        title: 'Missing Prompt',
        message: 'Please enter a prompt for soundtrack generation',
        type: 'error'
      });
      return;
    }

    setIsGenerating(true);

    try {
      if (activeTab === 'melody') {
        const response = await generateMelody();
        if (response.success) {
          setGeneratedMelodies(response.data.melodies);
          setProjectId(response.data.projectId);
          setActiveStep('result');
          
          // Phase 3: Check if soundtracks were saved to user library
          const savedToLibraryCount = response.data.melodies?.filter(melody => melody.userLibraryS3Url).length || 0;
          const libraryMessage = savedToLibraryCount > 0 
            ? ` Also saved ${savedToLibraryCount} to your soundtrack library!` 
            : '';
          
          // showNotification({
          //   title: '🎵 Success',
          //   message: `Generated ${response.data.count} soundtracks successfully!${libraryMessage}`,
          //   type: 'success'
          // });
        }
      }
    } catch (error) {
      console.error('Generation failed:', error);
      showNotification({
        title: 'Generation Failed',
        message: 'Failed to generate soundtrack. Please try again.',
        type: 'error'
      });
    } finally {
      setIsGenerating(false);
    }
  };

  // Handle audio playback with authentication
  const handlePlayPause = async (audioUrl: string, trackIndex: number) => {
    const trackId = `melody-${trackIndex}`;
    
    if (currentAudio && !currentAudio.paused && playingTrackId === trackId) {
      currentAudio.pause();
      setPlayingTrackId(null);
      setCurrentAudio(null);
    } else {
      if (currentAudio) {
        currentAudio.pause();
      }
      
      try {
        // Use proxy-audio API for S3 URLs with authentication
        const proxiedUrl = getProxiedAudioUrl(audioUrl);
        const authToken = localStorage.getItem('authToken');
        
        // Fetch the audio with auth token
        const response = await fetch(proxiedUrl, {
          headers: {
            ...(authToken && { 'Authorization': `Bearer ${authToken}` })
          }
        });
        
        if (!response.ok) {
          console.error('Failed to fetch audio:', response.status);
          showNotification({
            title: 'Playback Error',
            message: 'Failed to load audio',
            type: 'error'
          });
          return;
        }
        
        // Create blob URL from the response
        const audioBlob = await response.blob();
        const blobUrl = URL.createObjectURL(audioBlob);
        
        // Create and play audio from blob URL
        const audio = new Audio(blobUrl);
        audio.addEventListener('ended', () => {
          setPlayingTrackId(null);
          setCurrentAudio(null);
          URL.revokeObjectURL(blobUrl); // Clean up
        });
        
        audio.play();
        setPlayingTrackId(trackId);
        setCurrentAudio(audio);
      } catch (error) {
        console.error('Playback error:', error);
        showNotification({
          title: 'Playback Error',
          message: 'Failed to play audio',
          type: 'error'
        });
      }
    }
  };

  // Handle file upload
  const handleFileUpload = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      const file = event.target.files[0];
      
      if (!file.type.startsWith('audio/')) {
        showNotification({
          title: 'Invalid File',
          message: 'Please select an audio file',
          type: 'error'
        });
        return;
      }
      
      setUploadedFile(file);
      // Here you would handle the upload logic
    }
  };

  // Handle regeneration
  const handleRegenerate = () => {
    setActiveStep('generation');
    setGeneratedMelodies([]);
    if (currentAudio) {
      currentAudio.pause();
      setCurrentAudio(null);
    }
    setPlayingTrackId(null);
  };

  // Handle completion
  const handleComplete = () => {
    const selectedAudioUrl = generatedMelodies[selectedMelodyIndex]?.s3Url;
      
    if (selectedAudioUrl) {
      onAudioGenerated(selectedAudioUrl, projectId, 'melody');
    }
  };

  if (!isOpen) return null;

  return (
    <div className={styles.modalOverlay} onClick={handleClose}>
      <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <h2 className={styles.modalTitle}>
            {type === 'upload' ? t('modal.title.upload') : 
             type === 'melody' ? t('modal.title.melody') :
             activeStep === 'generation' ? t('modal.title.melody') : 'Generated Audio'}
          </h2>
          <button className={styles.closeButton} onClick={handleClose}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        {activeStep === 'generation' ? (
          <>
            {type !== 'upload' && (
              <Tabs value={activeTab} onChange={(value) => setActiveTab(value as 'melody' | 'library' | 'prompts')}>
                <Tabs.List>
                  <Tabs.Tab value="melody">
                    <FontAwesomeIcon icon={faMusic} style={{ marginRight: '8px' }} />
                    {t('tabs.melody')}
                  </Tabs.Tab>
                  <Tabs.Tab value="library">
                    <FontAwesomeIcon icon={faFolderOpen} style={{ marginRight: '8px' }} />
                    {t('tabs.library')}
                  </Tabs.Tab>
                  {!hideAllPromptsTab && (
                    <Tabs.Tab value="prompts">
                      <FontAwesomeIcon icon={faLightbulb} style={{ marginRight: '8px' }} />
                      {t('tabs.prompts')}
                    </Tabs.Tab>
                  )}
                </Tabs.List>

              <Tabs.Panel value="melody" pt="md">
                <Stack gap="xl">
                  <div>
                    <Text size="sm" fw={500} mb="xs">{t('melody.promptLabel')}</Text>
                    <Textarea
                      placeholder={t('melody.promptPlaceholder')}
                      value={melodyPrompt}
                      onChange={(e) => setMelodyPrompt(e.target.value)}
                      minRows={3}
                      maxRows={6}
                    />
                  </div>

                  <CustomSlider
                    value={melodyDuration}
                    onChange={setMelodyDuration}
                    min={1}
                    max={60}
                    step={1}
                    marks={[
                      { value: 10, label: '10s' },
                      { value: 30, label: '30s' },
                      { value: 60, label: '60s' }
                    ]}
                    label={t('melody.durationLabel')}
                    description={t('melody.durationCurrent', { duration: melodyDuration })}
                  />

                  <CustomSlider
                    value={melodyTemperature}
                    onChange={setMelodyTemperature}
                    min={0.1}
                    max={2.0}
                    step={0.1}
                    marks={[
                      { value: 0.5, label: t('melody.creativityConservative') },
                      { value: 1.0, label: t('melody.creativityBalanced') },
                      { value: 1.5, label: t('melody.creativityCreative') },
                      { value: 2.0, label: t('melody.creativityVeryCreative') }
                    ]}
                    label={t('melody.creativityLabel')}
                    description={t('melody.creativityDescription', { value: melodyTemperature })}
                  />

                  <CustomSlider
                    value={guidanceScale}
                    onChange={setGuidanceScale}
                    min={1.0}
                    max={10.0}
                    step={0.1}
                    marks={[
                      { value: 1.0, label: t('melody.promptAdherenceLoose') },
                      { value: 3.0, label: t('melody.promptAdherenceBalanced') },
                      { value: 6.0, label: t('melody.promptAdherenceStrict') },
                      { value: 10.0, label: t('melody.promptAdherenceVeryStrict') }
                    ]}
                    label={t('melody.promptAdherenceLabel')}
                    description={t('melody.promptAdherenceDescription', { value: guidanceScale })}
                  />
                </Stack>
              </Tabs.Panel>

              <Tabs.Panel value="library" pt="md">
                <SoundtrackLibraryPanel
                  onSoundtrackSelect={handleSoundtrackSelect}
                  currentProjectId={currentProjectId}
                  setActiveTab={setActiveTab}
                />
              </Tabs.Panel>

              {!hideAllPromptsTab && (
                <Tabs.Panel value="prompts" pt="md">
                  <Stack gap="md" style={{ minHeight: '400px' }}>
                    <div>
                      <Text size="sm" fw={500} mb="xs">{t('prompts.title')}</Text>
                      <Text size="xs" color="dimmed" mb="md">
                        {t('prompts.description')}
                      </Text>
                    </div>

                    <PromptBrowser
                      onPromptSelect={handleUsePrompt}
                      compact={true}
                      className={styles.modalPromptBrowser}
                      projectId={currentProjectId} // Use the renamed prop
                      height="350px"
                    />
                    
                    <div style={{ 
                      backgroundColor: 'rgba(59, 130, 246, 0.1)', 
                      padding: '12px', 
                      borderRadius: '8px',
                      border: '1px solid rgba(59, 130, 246, 0.2)'
                    }}>
                      <Text size="xs" color="dimmed">
                        {t('prompts.tip')}
                      </Text>
                    </div>
                  </Stack>
                </Tabs.Panel>
              )}
            </Tabs>
            )}

            {type === 'upload' && (
              <div className={styles.uploadSection}>
                <div className={styles.uploadArea} onClick={handleFileUpload}>
                  <FontAwesomeIcon icon={faUpload} size="3x" className={styles.uploadIcon} />
                  <h3>Upload Audio File</h3>
                  <p>Click to select an audio file from your device</p>
                  {uploadedFile && (
                    <div className={styles.uploadedFile}>
                      <strong>Selected: {uploadedFile.name}</strong>
                    </div>
                  )}
                </div>
                
                <input
                  type="file"
                  accept="audio/*"
                  ref={fileInputRef}
                  style={{ display: 'none' }}
                  onChange={handleFileChange}
                />
                
                {uploadedFile && (
                  <div className={styles.modalActions}>
                    <button onClick={handleClose} className={styles.cancelButton}>
                      Cancel
                    </button>
                    <Button onClick={() => {/* Handle upload */}} className={styles.primaryButton}>
                      Upload & Continue
                    </Button>
                  </div>
                )}
              </div>
            )}

            {type !== 'upload' && (
              <div className={styles.modalActions}>
                <button onClick={handleClose} className={styles.cancelButton}>
                  {t('modal.close')}
                </button>
                {activeTab !== 'prompts' && (
                  <Button 
                    onClick={handleGenerate} 
                    loading={isGenerating}
                    className={styles.primaryButton}
                  >
                    <FontAwesomeIcon icon={faWandMagicSparkles} style={{marginRight: "8px"}} />
                    {isGenerating ? t('melody.generating') : t('melody.generateButton')}
                  </Button>
                )}
              </div>
            )}
          </>
        ) : (
          // Result step
          <div className={styles.resultSection}>
            {generatedMelodies.length > 0 ? (
              <div className={styles.melodyResults}>
                <h3>{t('result.melodyTitle')}</h3>
                <p>{t('result.melodyDescription', { count: generatedMelodies.length })}</p>
                
                <div className={styles.melodyOptions}>
                  {generatedMelodies.map((melody, index) => (
                    <div 
                      key={melody.index}
                      className={`${styles.melodyOption} ${selectedMelodyIndex === index ? styles.selected : ''}`}
                      onClick={() => setSelectedMelodyIndex(index)}
                    >
                      <div className={styles.melodyHeader}>
                        <h4>{t('result.soundtrackNumber', { number: melody.index })}</h4>
                        <span className={styles.duration}>{melody.duration.toFixed(1)}s</span>
                      </div>
                      <div className={styles.melodyControls}>
                        <Button
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            handlePlayPause(melody.s3Url, index);
                          }}
                          leftSection={<FontAwesomeIcon icon={playingTrackId === `melody-${index}` ? faPause : faPlay} />}
                        >
                          {playingTrackId === `melody-${index}` ? t('result.pause') : t('result.play')}
                        </Button>
                        <Button
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleAudioDownload(
                              melody.s3Url,
                              `soundtrack_${melody.index}.mp3`,
                              token,
                              getProxiedAudioUrl,
                              {
                                onSuccess: () => showNotification({
                                  title: '✅ Downloaded',
                                  message: `Soundtrack ${melody.index} downloaded`,
                                  type: 'success'
                                }),
                                onError: (err) => showNotification({
                                  title: '❌ Download Failed',
                                  message: err.message,
                                  type: 'error'
                                })
                              }
                            );
                          }}
                          variant="outline"
                          leftSection={<FontAwesomeIcon icon={faDownload} />}
                        >
                          {t('result.download')}
                        </Button>
                      </div>
                      {selectedMelodyIndex === index && (
                        <div className={styles.selectedIndicator}>
                          <FontAwesomeIcon icon={faCheck} />
                        </div>
                      )}
                    </div>
                  ))}
                </div>
                
                {/* Phase 3: Show library save status and link */}
                {(() => {
                  const savedToLibraryCount = generatedMelodies.filter(melody => melody.userLibraryS3Url != null).length;
                  if (savedToLibraryCount > 0) {
                    return (
                      <div className={styles.libraryNotification}>
                        <div className={styles.libraryIcon}>
                          <FontAwesomeIcon icon={faCheck} />
                        </div>
                        <div className={styles.libraryText}>
                          <p>
                            <strong>{t('result.savedToLibrary')}</strong>
                          </p>
                          <p>
                            {savedToLibraryCount === 1 
                              ? t('result.savedSingle')
                              : t('result.savedMultiple', { count: savedToLibraryCount })
                            }
                          </p>
                        </div>
                        <Button
                          size="sm"
                          variant="gradient"
                          gradient={{ from: 'blue', to: 'purple', deg: 45 }}
                          className={styles.futuristicButtonSmall}
                          onClick={() => {
                            // Switch to soundtrack library tab
                            setActiveTab('library');
                            setActiveStep('generation');
                          }}
                          leftSection={<FontAwesomeIcon icon={faFolderOpen} />}
                        >
                          {t('result.viewLibrary')}
                        </Button>
                      </div>
                    );
                  }
                  return null;
                })()}
              </div>
            ) : null}

            <div className={styles.modalActions}>
              <button 
                onClick={handleRegenerate}
                className={styles.cancelButton}
              >
                <FontAwesomeIcon icon={faRefresh} style={{marginRight: "8px"}} />
                {t('result.regenerate')}
              </button>
              <Button 
                onClick={handleComplete}
                className={styles.primaryButton}
              >
                <FontAwesomeIcon icon={faCheck} style={{marginRight: "8px"}} />
                {t('result.complete')}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Soundtrack Library Panel Component
const SoundtrackLibraryPanel: React.FC<{
  onSoundtrackSelect: (soundtrack: any) => void;
  currentProjectId?: string;
  setActiveTab: (tab: 'melody' | 'library' | 'prompts') => void;
}> = ({ onSoundtrackSelect, currentProjectId, setActiveTab }) => {
  const { t } = useTranslation('audioModal');
  
  const [soundtracks, setSoundtracks] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedSoundtrack, setSelectedSoundtrack] = useState<number | null>(null);
  const [playingSoundtrackId, setPlayingSoundtrackId] = useState<string | null>(null);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editingPrompt, setEditingPrompt] = useState('');
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const { token } = useAuth();
  const { showNotification } = useNotification();

  useEffect(() => {
    fetchUserSoundtracks();
  }, []);

  const fetchUserSoundtracks = async () => {
    try {
      setIsLoading(true);
      const response = await fetch(getApiUrl('/music-generation/soundtracks'), {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` }),
        },
      });

      if (response.ok) {
        const result = await response.json();
        // Sort soundtracks by lastModified date (newest first)
        const sortedSoundtracks = (result.data?.soundtracks || []).sort((a: any, b: any) => {
          const dateA = new Date(a.lastModified).getTime();
          const dateB = new Date(b.lastModified).getTime();
          return dateB - dateA; // Descending order (newest first)
        });
        setSoundtracks(sortedSoundtracks);
      } else {
        console.error('Failed to fetch soundtracks');
      }
    } catch (error) {
      console.error('Error fetching soundtracks:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handlePlayPause = async (url: string, index: number) => {
    const soundtrackId = `library-${index}`;
    
    if (playingSoundtrackId === soundtrackId && audioRef.current && !audioRef.current.paused) {
      audioRef.current?.pause();
      setPlayingSoundtrackId(null);
    } else {
      try {
        // Use proxy-audio API for S3 URLs with authentication
        const proxiedUrl = getProxiedAudioUrl(url);
        
        // Fetch the audio with auth token
        const response = await fetch(proxiedUrl, {
          headers: {
            ...(token && { 'Authorization': `Bearer ${token}` })
          }
        });
        
        if (!response.ok) {
          console.error('Failed to fetch audio:', response.status);
          showNotification({
            title: 'Playback Error',
            message: 'Failed to load audio',
            type: 'error'
          });
          return;
        }
        
        // Create blob URL from the response
        const audioBlob = await response.blob();
        const blobUrl = URL.createObjectURL(audioBlob);
        
        if (audioRef.current) {
          audioRef.current.src = blobUrl;
          audioRef.current.play();
          setPlayingSoundtrackId(soundtrackId);
          setSelectedSoundtrack(index);
          
          audioRef.current.onended = () => {
            setPlayingSoundtrackId(null);
            setSelectedSoundtrack(null);
            URL.revokeObjectURL(blobUrl); // Clean up
          };
        }
      } catch (error) {
        console.error('Playback error:', error);
        showNotification({
          title: 'Playback Error',
          message: 'Failed to play audio',
          type: 'error'
        });
      }
    }
  };

  const handleEditStart = (index: number, currentPrompt: string) => {
    setEditingIndex(index);
    setEditingPrompt(currentPrompt);
  };

  const handleEditSave = async (index: number, filename: string) => {
    try {
      const response = await fetch(getApiUrl('/music-generation/soundtracks/update'), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` }),
        },
        body: JSON.stringify({
          filename: filename,
          prompt: editingPrompt
        }),
      });

      if (response.ok) {
        // Update local state
        const updatedSoundtracks = [...soundtracks];
        updatedSoundtracks[index].prompt = editingPrompt;
        setSoundtracks(updatedSoundtracks);
        
        // showNotification({
        //   title: '✅ Updated',
        //   message: 'Soundtrack name updated successfully',
        //   type: 'success'
        // });
      } else {
        throw new Error('Failed to update soundtrack');
      }
    } catch (error) {
      console.error('Error updating soundtrack:', error);
      showNotification({
        title: '❌ Error',
        message: 'Failed to update soundtrack name',
        type: 'error'
      });
    } finally {
      setEditingIndex(null);
      setEditingPrompt('');
    }
  };

  const handleEditCancel = () => {
    setEditingIndex(null);
    setEditingPrompt('');
  };

  const handleDelete = async (index: number, filename: string, prompt: string) => {
    if (!confirm(`Are you sure you want to delete "${prompt}"?`)) {
      return;
    }

    try {
      const response = await fetch(getApiUrl(`/music-generation/soundtracks/${encodeURIComponent(filename)}`), {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` }),
        },
      });

      if (response.ok) {
        // Remove from local state
        const updatedSoundtracks = soundtracks.filter((_, i) => i !== index);
        setSoundtracks(updatedSoundtracks);
        
      //   showNotification({
      //     title: '✅ Deleted',
      //     message: 'Soundtrack deleted successfully',
      //     type: 'success'
      //   });
      // } else {
        throw new Error('Failed to delete soundtrack');
      }
    } catch (error) {
      console.error('Error deleting soundtrack:', error);
      showNotification({
        title: '❌ Error',
        message: 'Failed to delete soundtrack',
        type: 'error'
      });
    }
  };

  if (isLoading) {
    return (
      <div style={{ textAlign: 'center', padding: '40px' }}>
        <Loader size="md" />
        <Text mt="md" color="dimmed">{t('library.loading')}</Text>
      </div>
    );
  }

  if (soundtracks.length === 0) {
    return (
      <div style={{ textAlign: 'center', padding: '40px' }}>
        <FontAwesomeIcon icon={faMusic} size="3x" style={{ color: '#666', marginBottom: '16px' }} />
        <Text size="lg" fw={500} mb="xs">{t('library.empty.title')}</Text>
        <Text color="dimmed" size="sm">
          {t('library.empty.message')}
        </Text>
        <Button 
          mt="md" 
          onClick={() => setActiveTab('melody')}
          size="md"
          variant="gradient"
          gradient={{ from: 'blue', to: 'purple', deg: 45 }}
          className={styles.futuristicButton}
          leftSection={<FontAwesomeIcon icon={faMusic} />}
        >
          {t('library.empty.button')}
        </Button>
      </div>
    );
  }

  return (
    <Stack gap="md" style={{ minHeight: '400px' }}>
      <div>
        <Text size="sm" fw={500} mb="xs">{t('library.title', { count: soundtracks.length })}</Text>
        <Text size="xs" color="dimmed" mb="md">
          {t('library.description')}
        </Text>
      </div>

      <div className={styles.soundtrackGrid}>
        {soundtracks.map((soundtrack, index) => (
          <div 
            key={`${soundtrack.filename}-${index}`}
            className={`${styles.soundtrackCard} ${selectedSoundtrack === index ? styles.selected : ''}`}
          >
            <div className={styles.soundtrackHeader}>
              {editingIndex === index ? (
                <input
                  type="text"
                  value={editingPrompt}
                  onChange={(e) => setEditingPrompt(e.target.value)}
                  className={styles.editInput}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      handleEditSave(index, soundtrack.filename);
                    } else if (e.key === 'Escape') {
                      handleEditCancel();
                    }
                  }}
                  autoFocus
                />
              ) : (
                <h4>{soundtrack.prompt || 'Untitled Soundtrack'}</h4>
              )}
              <div className={styles.headerActions}>
                <span className={styles.duration}>
                  {soundtrack.duration ? `${soundtrack.duration.toFixed(1)}s` : 'Unknown'}
                </span>
                {editingIndex === index ? (
                  <div className={styles.editActions}>
                    <button
                      className={styles.editSaveButton}
                      onClick={() => handleEditSave(index, soundtrack.filename)}
                      title={t('library.saveButton')}
                    >
                      <FontAwesomeIcon icon={faCheck} />
                    </button>
                    <button
                      className={styles.editCancelButton}
                      onClick={handleEditCancel}
                      title={t('library.cancelButton')}
                    >
                      <FontAwesomeIcon icon={faTimes} />
                    </button>
                  </div>
                ) : (
                  <div className={styles.cardActions}>
                    <button
                      className={styles.downloadButton}
                      onClick={async (e) => {
                        e.stopPropagation();
                        try {
                          await downloadAudio(soundtrack.s3Url, {
                            filename: `${soundtrack.prompt || 'soundtrack'}.mp3`,
                            onStart: () => {
                              console.log('📥 [EXPORT AUDIO] Calling');
                            },
                            onComplete: () => {
                              console.log('✅ [SOUNDTRACK] soundtrack downloaded successfully');
                              showNotification({
                                title: 'Audio Ready!',
                                message: 'Your audio has been downloaded',
                                type: 'success'
                              });
                            },
                            onError: (error) => {
                              console.error('❌ [SOUNDTRACK] Failed to download Audio:', error);
                              showNotification({
                                title: 'Download Failed',
                                message: 'Audio was created but download failed. Use the download button to try again.',
                                type: 'warning'
                              });
                            }
                          });
                        
                        } catch (error) {
                          console.error('Download failed:', error);
                          showNotification({
                            title: '❌ Error',
                            message: error instanceof Error ? error.message : 'Failed to download soundtrack',
                            type: 'error'
                          });
                        }
                      }}
                      title={t('library.downloadButton')}
                    >
                      <FontAwesomeIcon icon={faDownload} />
                    </button>
                    <button
                      className={styles.editButton}
                      onClick={() => handleEditStart(index, soundtrack.prompt || '')}
                      title={t('library.editButton')}
                    >
                      <FontAwesomeIcon icon={faEdit} />
                    </button>
                    <button
                      className={styles.deleteButton}
                      onClick={() => handleDelete(index, soundtrack.filename, soundtrack.prompt || 'Untitled')}
                      title={t('library.deleteButton')}
                    >
                      <FontAwesomeIcon icon={faTrash} />
                    </button>
                  </div>
                )}
              </div>
            </div>
            
            <div className={styles.soundtrackMeta}>
              <Text size="xs" color="dimmed">
                {new Date(soundtrack.lastModified).toLocaleDateString()}
              </Text>
              <Text size="xs" color="dimmed">
                {(soundtrack.size / (1024 * 1024)).toFixed(1)} MB
              </Text>
            </div>

            <div className={styles.soundtrackControls}>
              <Button
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  handlePlayPause(soundtrack.s3Url, index);
                }}
                variant="outline"
                className={styles.playButton}
                leftSection={<FontAwesomeIcon icon={playingSoundtrackId === `library-${index}` ? faPause : faPlay} />}
              >
                {playingSoundtrackId === `library-${index}` ? 'Pause' : t('library.playButton')}
              </Button>
              <Button
                size="sm"
                variant="gradient"
                gradient={{ from: 'green', to: 'teal', deg: 45 }}
                className={styles.futuristicButtonSmall}
                onClick={() => onSoundtrackSelect(soundtrack)}
                leftSection={<FontAwesomeIcon icon={faCheck} />}
              >
                {t('library.useButton')}
              </Button>
            </div>
          </div>
        ))}
      </div>

      <audio ref={audioRef} />
    </Stack>
  );
};

export default AudioModal;